import _ from 'lodash'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { listWaitingRooms, selectWaitingRooms, selectWaitingRoomSearchResult } from '@/redux/reducers/waiting-room.reducer'

import { useCreateConsultationFromOrder } from '../hooks/useCreateConsultationFromOrder'
import { countAvailableDoctors, toDoctorIds, useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

dayjs.extend(isBetween)

// Own component because the live-status hook must run per room row.
function AvailableDoctorsText({ waitingRoomId, doctorIds }: { waitingRoomId: string; doctorIds: string[] }) {
  const { t } = useTranslation()
  const { statuses, isReady } = useRoomDoctorStatuses(waitingRoomId)
  const availableDoctors = countAvailableDoctors(doctorIds, statuses)
  return <>{`${isReady ? availableDoctors : '—'} ${t('page.agenda.waitingRoomPicker.availableDoctors')}`}</>
}

type WaitingRoomPickerProps = {
  order: MeetingLinkOrder
  onBack: () => void
  onComplete: () => void
}

export default function WaitingRoomPicker({ order, onBack, onComplete }: WaitingRoomPickerProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const waitingRooms = useAppSelector(selectWaitingRooms)
  const waitingRoomSearchResult = useAppSelector(selectWaitingRoomSearchResult)
  const totalCount = useMemo(() => _.get(waitingRoomSearchResult, 'totalCount', 0), [waitingRoomSearchResult])

  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')

  const { execute, isLoading } = useCreateConsultationFromOrder({
    order,
    onSuccess: onComplete,
    navigateTo: (id) => `/consultations/${id}/live`,
  })

  useEffect(() => {
    dispatch(listWaitingRooms({}, { limit: 10, skip: 0 }))
  }, [dispatch])

  const waitingRoomsData = useMemo(() => {
    const rooms = _.map(waitingRooms, (waitingRoom) => {
      const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
      let isOpening = false
      if (openingHour) {
        const { periods } = openingHour
        const now = dayjs()
        isOpening = _.some(periods, (period) => {
          const openAt = dayjs(period.open, 'HH:mm')
          const closeAt = dayjs(period.close, 'HH:mm')
          return (now.isAfter(openAt) || now.isSame(openAt)) && (now.isBefore(closeAt) || now.isSame(closeAt))
        })
      }
      return {
        id: waitingRoom._id,
        doctorIds: toDoctorIds(waitingRoom.doctors),
        isOpening,
        name: waitingRoom.name,
        avgWaitingTime: waitingRoom.avgWaitingTime,
        matchesSpeciality: false, // TODO: match against order.speciality when WR has speciality field
      }
    })

    // Sort speciality-matching rooms first (when implemented)
    return _.sortBy(rooms, [(r) => (r.matchesSpeciality ? 0 : 1)])
  }, [waitingRooms])

  const debouncedSearch = useMemo(() => _.debounce((text: string) => dispatch(listWaitingRooms({ name: text }, { limit: 10, skip: 0 })), 300), [dispatch])

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const handleSearch = (text: string) => {
    setSearchText(text)
    debouncedSearch(text)
  }

  const handleSelect = (roomId: string, isOpening: boolean) => {
    if (!isOpening) return
    setSelectedRoomId(roomId === selectedRoomId ? null : roomId)
  }

  const handleConfirm = () => {
    if (!selectedRoomId) return
    execute({
      patient: order.createdBy._id,
      type: ConsultationType.MeetingOnly,
      status: ConsultationStatus.Scheduled,
      waitingRoom: selectedRoomId,
      startDate: dayjs().toISOString(),
      speciality: order.speciality,
    })
  }

  return (
    <Stack spacing={2} sx={{ minHeight: 300 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
          {t('common.back')}
        </Button>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('page.agenda.waitingRoomPicker.title')}
        </Typography>
      </Stack>

      <TextField size="small" placeholder={t('common.search')} value={searchText} onChange={(e) => handleSearch(e.target.value)} fullWidth />

      {waitingRoomsData.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.noResults')}
          </Typography>
        </Box>
      ) : (
        <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {waitingRoomsData.map((room) => (
            <ListItemButton
              key={room.id}
              selected={selectedRoomId === room.id}
              disabled={!room.isOpening}
              onClick={() => handleSelect(room.id, room.isOpening)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <span>{room.name}</span>
                    <Chip
                      size="small"
                      label={room.isOpening ? t('common.open') : t('common.closed')}
                      color={room.isOpening ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Stack>
                }
                secondary={
                  <>
                    <AvailableDoctorsText waitingRoomId={room.id} doctorIds={room.doctorIds} />
                    {` · ${room.avgWaitingTime ?? '?'} min`}
                  </>
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" onClick={onBack}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedRoomId || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t('page.agenda.waitingRoomPicker.confirm')}
        </Button>
      </Stack>
    </Stack>
  )
}
